<template>
  <header class="adm-topbar">
    <button
      type="button"
      class="adm-topbar__burger"
      aria-label="Abrir menú"
      @click="$emit('open-drawer')"
    >
      <Menu :size="22" />
    </button>

    <h1 class="adm-topbar__title">{{ pageTitle }}</h1>

    <div class="adm-topbar__user">
      <span class="adm-topbar__avatar" aria-hidden="true">{{ initials }}</span>
      <span class="adm-topbar__name">{{ name }}</span>
      <button
        type="button"
        class="adm-topbar__logout"
        :disabled="leaving"
        @click="onLogout"
      >
        <LogOut :size="16" />
        <span>{{ leaving ? 'Saliendo…' : 'Salir' }}</span>
      </button>
    </div>
  </header>
</template>

<script>
/**
 * Barra superior del panel — plan 27_09_2026 §4.2.
 *
 * Aporta las dos cosas que el admin no tenía: saber quién está dentro
 * (el `user` de `GET /verify`, que hasta ahora se descartaba) y poder salir
 * (la cookie es `httpOnly`, así que "Salir" llama a `POST /auth/logout`).
 */
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Menu, LogOut } from 'lucide-vue-next';
import { findNavItem } from '@/config/adminNav.js';
import { useAuthUser } from '@/composables/useAuthUser.js';
import { notify } from '@/composables/useAdmToast.js';

export default {
  name: 'AdminTopbar',
  components: { Menu, LogOut },
  emits: ['open-drawer'],
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { user, logout, displayName } = useAuthUser();
    const leaving = ref(false);

    const pageTitle = computed(
      () => route.meta?.title || findNavItem(route)?.label || 'Administración',
    );

    const name = computed(() => displayName(user.value));

    const initials = computed(() => {
      const value = name.value.trim();
      if (!value) return '·';
      const parts = value.split(/[\s@._-]+/).filter(Boolean);
      return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
    });

    async function onLogout() {
      leaving.value = true;
      const { success, error } = await logout();
      leaving.value = false;
      if (!success) notify.error(error || 'No se pudo cerrar la sesión.');
      router.push('/login');
    }

    return { pageTitle, name, initials, leaving, onLogout, Menu, LogOut };
  },
};
</script>

<style scoped>
.adm-topbar {
  display: flex;
  align-items: center;
  gap: var(--adm-gap);
  height: var(--adm-topbar-height);
  padding-inline: var(--adm-content-pad);
  background-color: var(--adm-surface);
  border-bottom: 1px solid var(--adm-border);
  position: sticky;
  top: 0;
  z-index: var(--adm-z-topbar);
}

.adm-topbar__burger {
  display: none;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding: 0;
  background: none;
  border: 1px solid var(--adm-border);
  border-radius: var(--adm-radius-sm);
  color: var(--color-text-strong);
  cursor: pointer;
}

.adm-topbar__title {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-family: var(--font-subtitle);
  font-weight: 700;
  font-size: 1.05rem;
  color: var(--color-title);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.adm-topbar__user {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;
}

.adm-topbar__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--color-brand);
  color: var(--color-white);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.adm-topbar__name {
  font-size: var(--adm-font-sm);
  color: var(--color-text-strong);
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.adm-topbar__logout {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.7rem;
  background: none;
  border: 1px solid var(--adm-border);
  border-radius: var(--adm-radius-sm);
  color: var(--color-text);
  font-family: var(--font-body);
  font-size: var(--adm-font-sm);
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.adm-topbar__logout:hover:not(:disabled) {
  border-color: var(--adm-danger-fg);
  color: var(--adm-danger-fg);
}

.adm-topbar__logout:disabled { opacity: 0.6; cursor: progress; }

@media (max-width: 767px) {
  .adm-topbar__burger { display: inline-flex; }
  .adm-topbar__name { display: none; }
  .adm-topbar__logout span { display: none; }
}
</style>
